import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ChevronDown, UserX, Loader2, Search } from 'lucide-react';
import { useAuth, API } from '../../App';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';

/**
 * Inline driver picker shown directly in the rides table.
 * Click the cell -> popover with searchable driver list -> pick to assign.
 *
 * Props:
 *  - ride: ride object (must contain id, assigned_driver_id, assigned_driver_name)
 *  - drivers: [{id, name, role}]
 *  - onUpdated(updatedRide): callback after successful update (gets the new ride from server)
 */
export default function InlineDriverPicker({ ride, drivers, onUpdated }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter((d) => (d.name || '').toLowerCase().includes(q));
  }, [drivers, query]);

  const assign = async (driverId) => {
    if (busy) return;
    // No-op if user picks the same driver again
    if (driverId === (ride.assigned_driver_id || null)) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      // Pre-check driver attendance for the ride's date (warn-only, never block)
      if (driverId && ride.date_time) {
        try {
          const datePart = new Date(ride.date_time).toISOString().slice(0, 10);
          const att = await axios.get(`${API}/driver-attendance/status`, {
            params: { driver_id: driverId, date: datePart },
            headers: { Authorization: `Bearer ${token}` },
          });
          const st = att.data?.status;
          if (st && ['day_off', 'sick_leave', 'holiday'].includes(st)) {
            const label = { day_off: 'Day Off', sick_leave: 'Sick Leave', holiday: 'Holiday' }[st];
            const drv = drivers.find((d) => d.id === driverId);
            toast.warning(`${drv?.name || 'Driver'} is on ${label} on ${datePart} — assignment will proceed`);
          }
        } catch (e) {
          // Non-blocking
        }
      }
      const res = await axios.put(
        `${API}/rides/${ride.id}`,
        { assigned_driver_id: driverId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data;
      if (onUpdated) onUpdated(updated);
      const name = driverId
        ? drivers.find((d) => d.id === driverId)?.name || 'driver'
        : null;
      toast.success(driverId ? `Driver assigned: ${name}` : 'Driver unassigned');
      setOpen(false);
      setQuery('');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to update driver';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            // Don't bubble to row click handler
            e.stopPropagation();
          }}
          className="group/picker inline-flex items-center gap-1.5 px-2 py-1 -mx-2 -my-1 rounded hover:bg-slate-100 transition-colors text-sm text-slate-700 max-w-full"
          data-testid={`inline-driver-picker-${ride.id}`}
          title="Click to assign driver"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500 flex-shrink-0" />
          ) : null}
          <span className={`truncate ${!ride.assigned_driver_name ? 'text-slate-400 italic' : 'font-medium'}`}>
            {ride.assigned_driver_name || 'Unassigned'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0 opacity-0 group-hover/picker:opacity-100 transition-opacity" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
        data-testid={`inline-driver-picker-popover-${ride.id}`}
      >
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              autoFocus
              placeholder="Search driver…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-7 h-8 text-sm"
              data-testid={`inline-driver-picker-search-${ride.id}`}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          <button
            type="button"
            onClick={() => assign(null)}
            disabled={busy}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-100 text-left ${
              !ride.assigned_driver_id ? 'bg-slate-50 font-medium' : ''
            }`}
            data-testid={`inline-driver-picker-unassign-${ride.id}`}
          >
            <UserX className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-slate-600 italic">Unassigned</span>
            {!ride.assigned_driver_id && (
              <Check className="h-3.5 w-3.5 text-emerald-600 ml-auto" />
            )}
          </button>
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-slate-400 text-center">
              No drivers found
            </div>
          ) : (
            filtered.map((d) => {
              const isCurrent = d.id === ride.assigned_driver_id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => assign(d.id)}
                  disabled={busy}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-100 text-left ${
                    isCurrent ? 'bg-slate-50 font-medium' : ''
                  }`}
                  data-testid={`inline-driver-picker-option-${ride.id}-${d.id}`}
                >
                  <span className="truncate">{d.name}</span>
                  {d.role === 'admin-driver' && (
                    <span className="text-[10px] uppercase tracking-wide px-1 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                      Admin
                    </span>
                  )}
                  {isCurrent && (
                    <Check className="h-3.5 w-3.5 text-emerald-600 ml-auto" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
